import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridRowSpacingParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import useGetStacks from '@src/hooks/api/queries/modes/useGetStacks';
import useStackMutations from '@src/hooks/api/mutations/useStackMutations';
import { ControlModes, Stack } from '@src/types/Stacks';
import { stacksSx } from '@src/components/_shared/sxStyles/Config/configTableList';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import StackModal from './StackModal';

import StacksToolbar from './components/StacksToolbar';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@src/constants/queryKeys';
import './Stacks.scss';

import { ImportControlModeTemplateResponseDto } from '@src/types/ControlModes';
import storeConnector from '@store/storeConnector';
import { UserRole } from '@src/services/auth/roles';
import { engineerOrAdmin } from '@src/services/auth';
import ImportResultDialog from '../ConfigurableModes/components/ImportResultDialog';

// Extended Stack type with UI-specific properties
export interface StackExtended extends Stack {
  isNew?: boolean;
  remove?: boolean;
}

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface StackRow {
  id: string;
  name: string;
  controlModesCount: number;
  controlModesNames: string;
  controlModes: ControlModes[];
  updatedAt?: Date;
}

interface StackListProps {
  role: UserRole;
}

const StackList: React.FC<StackListProps> = ({ role }) => {
  const canEdit = engineerOrAdmin(role);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStackId, setSelectedStackId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [stackToDelete, setStackToDelete] = useState<string | null>(null);
  const [importResult, setImportResult] =
    useState<ImportControlModeTemplateResponseDto | null>(null);
  const [importResultDialogOpen, setImportResultDialogOpen] = useState(false);
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();

  const { data: stacks, isLoading } = useGetStacks();
  const { deleteStack } = useStackMutations(stackToDelete);

  const handleCreateClick = () => {
    setSelectedStackId(null);
    setModalOpen(true);
  };

  const handleEditClick = (id: string) => {
    setSelectedStackId(id);
    setModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setStackToDelete(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (stackToDelete) {
      try {
        await deleteStack.mutateAsync({});
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.stacks],
        });
        setConfirmDialogOpen(false);
        setStackToDelete(null);
      } catch (error) {
        console.error('Failed to delete stack:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setStackToDelete(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedStackId(null);
  };

  const handleImportSuccess = (
    result: ImportControlModeTemplateResponseDto,
  ) => {
    setImportResult(result);
    setImportResultDialogOpen(true);
  };

  const handleImportResultDialogClose = () => {
    setImportResultDialogOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      editable: false,
    },

    {
      field: 'controlModesNames',
      headerName: 'Control Modes',
      flex: 1,
      width: 300,
      editable: false,
      renderCell: (params) => {
        const controlModes: ControlModes[] = params.row.controlModesNames;
        if (!controlModes || controlModes.length === 0) {
          return '-';
        }
        return params.row.controlModesNames;
      },
    },

    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 200,
      editable: false,
      renderCell: (params) => {
        const updatedAt = params.row.updatedAt;
        if (!updatedAt) {
          return '-';
        }
        return dayjs.utc(updatedAt).local().format('MMM DD, YYYY, hh:mm A');
      },
    },
    ...(canEdit
      ? [
          {
            field: 'actions',
            type: 'actions' as const,
            headerName: 'Actions',
            width: 150,
            getActions: ({ id }: GridRowParams) => [
              <GridActionsCellItem
                key='edit'
                icon={<EditIcon />}
                label='Edit'
                onClick={() => handleEditClick(id.toString())}
                color='inherit'
              />,
              <GridActionsCellItem
                key='delete'
                icon={<DeleteIcon />}
                label='Delete'
                onClick={() => handleDeleteClick(id.toString())}
                color='inherit'
              />,
            ],
          },
        ]
      : []),
  ];

  const rows: StackRow[] =
    stacks?.map((stack: Stack) => ({
      id: stack.id,
      name: stack.name,
      controlModesCount: stack.controlModes?.length || 0,
      controlModesNames: stack.controlModes
        ?.map((mode) => mode.name)
        .join(', '),
      controlModes: stack.controlModes || [],
      updatedAt: stack.updatedAt,
    })) || [];

  return (
    <main className='stacks__main'>
      <section className='stacks__wrapper'>
        <DataGridPro
          rows={rows}
          columns={columns}
          apiRef={apiRef}
          hideFooter
          loading={isLoading}
          getRowClassName={() => 'MuiDataGrid-clickable stacks-row'}
          getRowId={(row) => row.id}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            density: 'comfortable',
          }}
          slots={{
            toolbar: () => (
              <StacksToolbar
                handleAdd={handleCreateClick}
                onImportSuccess={handleImportSuccess}
                canEdit={canEdit}
              />
            ),
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
          }}
          getRowSpacing={(_params: GridRowSpacingParams) => ({
            top: 9,
            bottom: 4,
          })}
          sx={stacksSx.wrapper}
        />
      </section>

      {modalOpen && canEdit && (
        <StackModal
          stackId={selectedStackId || undefined}
          open={modalOpen}
          onClose={handleModalClose}
        />
      )}

      {/* Confirmation Dialog for Delete */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Are you sure you want to delete this stack? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color='primary'>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color='error' autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ImportResultDialog
        open={importResultDialogOpen}
        onClose={handleImportResultDialogClose}
        result={importResult}
      />
    </main>
  );
};

export default storeConnector(StackList, {
  user: ['role'],
});
